import { supabase } from "@/integrations/supabase/client";
import type { PersonalizationPreferences } from "@/hooks/usePersonalizationPreferences";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";

export interface UserPreferencesRecord {
  id: string;
  organization_id: string;
  user_id: string;
  preferences: PersonalizationPreferences;
  created_at: string;
  updated_at: string;
}

type UserPreferencesFetchResult = Pick<UserPreferencesRecord, "preferences" | "updated_at">;
type UserPreferencesUpsertResult = Pick<UserPreferencesRecord, "preferences">;
const blockedUserPreferenceScopes = new Set<string>();

function buildScopeKey(organizationId: string, userId: string) {
  return `${organizationId}:${userId}`;
}

function isUserPreferenceScopeBlocked(organizationId: string, userId: string) {
  return blockedUserPreferenceScopes.has(buildScopeKey(organizationId, userId));
}

function markUserPreferenceScopeBlocked(organizationId: string, userId: string) {
  blockedUserPreferenceScopes.add(buildScopeKey(organizationId, userId));
}

export async function fetchUserPreferences(organizationId: string, userId: string) {
  if (isUserPreferenceScopeBlocked(organizationId, userId)) return null;

  const { data, error } = (await supabase
    .from("user_preferences")
    .select("preferences, updated_at")
    .eq("organization_id", organizationId)
    .eq("user_id", userId)
    .maybeSingle()) as { data: UserPreferencesFetchResult | null; error: unknown };

  if (error) {
    if (isSupabaseUnavailableError(error)) {
      markUserPreferenceScopeBlocked(organizationId, userId);
      return null;
    }

    if (!isSupabaseUnavailableError(error)) {
      console.debug("[personalization] unable to fetch preferences", error);
    }
    throw error;
  }

  return data;
}

export async function upsertUserPreferences(
  organizationId: string,
  userId: string,
  preferences: PersonalizationPreferences,
): Promise<UserPreferencesUpsertResult | null> {
  if (isUserPreferenceScopeBlocked(organizationId, userId)) return null;

  // allow-cross-tenant: upsert payload carries organization_id and the
  // (organization_id, user_id) conflict target keys the row; trailing
  // .eq() does not apply to upsert ON CONFLICT semantics.
  const { data, error } = await supabase
    .from("user_preferences")
    .upsert(
      {
        organization_id: organizationId,
        user_id: userId,
        preferences,
      },
      {
        onConflict: "organization_id,user_id",
      },
    )
    .select("preferences")
    .single();

  if (error) {
    if (isSupabaseUnavailableError(error)) {
      markUserPreferenceScopeBlocked(organizationId, userId);
      return null;
    }

    if (!isSupabaseUnavailableError(error)) {
      console.debug("[personalization] unable to persist preferences", error);
    }
    throw error;
  }

  return data;
}

/**
 * Atomically advances lastSeenChangelogId via the set_personalization_
 * changelog_seen RPC, instead of the general full-document upsertUserPreferences
 * path above. That path only ever compares against THIS client's own
 * in-memory snapshot before writing the whole document back -- a stale tab
 * (e.g. one left open across a deploy, still running an older JS bundle)
 * can pass that check against its own stale state and regress a value a
 * fresher session already advanced. The RPC resolves the race server-side,
 * in one locked transaction, and can never regress the field (Codex P2,
 * PR #6347). Returns the field's value AFTER the call, which may not be
 * `changelogId` itself if a concurrent write already advanced past it --
 * callers should adopt whatever comes back rather than assuming their own
 * value won.
 */
export async function setChangelogLastSeen(
  organizationId: string,
  userId: string,
  changelogId: string,
): Promise<string | null> {
  if (isUserPreferenceScopeBlocked(organizationId, userId)) return null;

  const { data, error } = await supabase.rpc("set_personalization_changelog_seen", {
    p_organization_id: organizationId,
    p_user_id: userId,
    p_changelog_id: changelogId,
  });

  if (error) {
    if (isSupabaseUnavailableError(error)) {
      markUserPreferenceScopeBlocked(organizationId, userId);
      return null;
    }

    console.debug("[personalization] unable to persist changelog seen state", error);
    throw error;
  }

  return (data as { lastSeenChangelogId?: string } | null)?.lastSeenChangelogId ?? null;
}
